import React from "react";
import { withTenantContext, type AttachmentView } from "@/api";
import { cx } from "@/lib";
import { useT } from "../../i18n";
import styles from "./email-review.module.css";

/**
 * What the customer attached (#594). Refused parts are listed too, greyed out and with the reason
 * spelled out: an operator answering "the guest list is attached" has to know that a guest list
 * arrived even when policy would not keep it.
 */

/** Binary sizes, one decimal place — a file manager's units, not marketing megabytes. */
export function formatByteSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/** A short, recognizable label for the kind of file, derived from the server's content type. */
export function attachmentKind(contentType: string): string {
  const type = contentType.toLowerCase();
  if (type === "application/pdf") return "PDF";
  // A refused row keeps the sender's declared type, which can be a structured subtype like
  // `image/svg+xml`; the label is the family, not the whole spelling.
  if (type.startsWith("image/")) {
    return type.slice("image/".length).split("+")[0]!.toUpperCase() || "FILE";
  }
  if (type === "text/csv") return "CSV";
  if (type === "text/plain") return "TXT";
  if (type === "text/calendar") return "ICS";
  if (type === "application/zip") return "ZIP";
  if (type === "message/rfc822") return "EML";
  if (type.includes("word") || type.includes("opendocument.text")) return "DOC";
  if (type.includes("sheet") || type.includes("excel")) return "XLS";
  if (type.includes("presentation") || type.includes("powerpoint")) return "PPT";
  return "FILE";
}

const REJECTION_KEY: Record<string, string> = {
  too_large: "ws.attachments.rejected.tooLarge",
  message_budget_exhausted: "ws.attachments.rejected.budget",
  unsupported_type: "ws.attachments.rejected.type",
  empty: "ws.attachments.rejected.empty",
};

export function AttachmentStrip({
  requestId,
  attachments,
}: {
  requestId: string;
  attachments: AttachmentView[];
}) {
  const t = useT();
  if (attachments.length === 0) return null;

  return (
    <section className={styles.attachments} data-testid="attachment-strip">
      <h3 className={styles.attachmentsTitle}>
        {t("ws.attachments.title")} ({attachments.length})
      </h3>
      <ul className={styles.attachmentList}>
        {attachments.map((attachment) => {
          const meta = `${attachmentKind(attachment.contentType)} · ${formatByteSize(attachment.byteSize)}`;
          const reason = attachment.rejection
            ? t(REJECTION_KEY[attachment.rejection] ?? "ws.attachments.rejected.type")
            : null;
          return (
            <li
              key={attachment.id}
              className={cx(styles.attachment, !attachment.available && styles.attachmentRefused)}
              data-testid="attachment-item"
              data-available={attachment.available ? "true" : "false"}
            >
              <span className={styles.attachmentKind} aria-hidden="true">
                {attachmentKind(attachment.contentType)}
              </span>
              {attachment.available
                ? (
                  <a
                    className={styles.attachmentBody}
                    data-testid="attachment-download"
                    // A same-origin, session-authenticated download. `download` asks the
                    // browser to save rather than navigate, and the response's own
                    // Content-Disposition and nosniff headers decide the rest.
                    href={withTenantContext(`/api/requests/${requestId}/attachments/${attachment.id}`)}
                    download={attachment.filename}
                    rel="noopener"
                    title={attachment.filename}
                  >
                    <span className={styles.attachmentName}>{attachment.filename}</span>
                    <span className={styles.attachmentMeta}>{meta}</span>
                    {reason && (
                      <span className={styles.attachmentReason} data-testid="attachment-rejection">
                        {reason}
                      </span>
                    )}
                  </a>
                )
                : (
                  <span className={styles.attachmentBody}>
                    <span className={styles.attachmentName} title={attachment.filename}>
                      {attachment.filename}
                    </span>
                    <span className={styles.attachmentMeta}>{meta}</span>
                    {reason && (
                      <span className={styles.attachmentReason} data-testid="attachment-rejection">
                        {reason}
                      </span>
                    )}
                  </span>
                )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
